import { Button } from "@admin/components/atoms/Button";
import { Input, Option, Select } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import {
  FoodControlPointInstanceDto,
  InstanceStatus,
  PromiseResult,
  updateFoodControlPointInstance,
} from "@enos-monorepo/lib";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface ExplanationProps {
  selectedInstances: FoodControlPointInstanceDto[];
}

export function Explanation({ selectedInstances }: ExplanationProps) {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState<string | undefined>();
  const [remark, setRemark] = useState<string | undefined>();
  const [isPending, setIsPending] = useState(false);

  const options: Option[] = [
    {
      value: t("food.Not Performed Vacation"),
      label: t("food.Not Performed Vacation"),
    },
    {
      value: t("food.Not Performed illness"),
      label: t("food.Not Performed illness"),
    },
    {
      value: t("food.Not Performed internet"),
      label: t("food.Not Performed internet"),
    },
    {
      value: t("food.Not Performed Longer relevant"),
      label: t("food.Not Performed Longer relevant"),
    },
    {
      value: t("food.Not Performed Lack of Time"),
      label: t("food.Not Performed Lack of Time"),
    },
    {
      value: t("food.Not Performed forgotten"),
      label: t("food.Not Performed forgotten"),
    },
    {
      value: t("food.Not Performed organizational changes"),
      label: t("food.Not Performed organizational changes"),
    },
  ];

  const queryClient = useQueryClient();

  const handleSaveClick = async () => {
    if (!selectedReason) return;

    setIsPending(true);

    const updatePromises = selectedInstances.map((instance) =>
      updateFoodControlPointInstance({
        ...instance,
        status: InstanceStatus.overdueSubmitted,
        reason: selectedReason,
        remark: remark,
        updatedDate: instance.endDate,
      }),
    );

    const results = await Promise.allSettled(updatePromises);

    const successes = results.filter(
      (result) => result.status === PromiseResult.Fulfilled,
    );

    const failures = results.filter(
      (result) => result.status === PromiseResult.Rejected,
    );

    if (successes.length > 0) {
      toast.success(
        t("food.Overdue Update Success", { length: successes.length }),
      );
    }

    if (failures.length > 0) {
      toast.error(t("food.Overdue Update Failed", { length: failures.length }));
    }

    queryClient.invalidateQueries({
      queryKey: ["foodControlPoints", selectedInstances[0]?.companyLocationId],
    });
    queryClient.invalidateQueries({
      queryKey: [
        "foodControlPointInstances",
        selectedInstances[0]?.companyLocationId,
      ],
    });

    setRemark("");
    setIsPending(false);
  };

  return (
    <div className="  bg-white rounded-xl h-fit">
      <div className="flex flex-row gap-10 items-center border-b-[1px] border-b-gray-300">
        <h1 className="card-title p-5">
          {t("food.Enter Explanation for Selected Overdue Controls")}
        </h1>
        <i className="ki-outline ki-information-2 text-2xl"></i>
      </div>

      <div className="px-7.5 border-b-[1px] border-b-gray-300">
        <Label title={t("food.Choose a Reason")}>
          <Select
            options={options}
            onChange={(e) => setSelectedReason(e.target.value)}
          />
        </Label>
      </div>

      <div className="px-7.5 border-b-[1px] border-b-gray-300">
        <Label title={t("food.Remark")}>
          <Input
            placeholder={t("food.Enter Remark")}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
          />
        </Label>
      </div>

      <div className="pr-7.5 py-2.5 flex justify-end gap-2.5">
        <Button variant="secondary" disabled={isPending}>
          {t("common.Discard")}
        </Button>
        <Button
          disabled={selectedInstances?.length < 1}
          loading={isPending}
          onClick={handleSaveClick}
        >
          {t("food.Save Explanation")}
        </Button>
      </div>
    </div>
  );
}
